import { gql } from "@apollo/client";

// Shared field sets ------------------------------------------------------------

const CONVERSATION_FIELDS = `
  id waId displayNumber contactName profileName leadId leadContactId clientId linkSource
  assignedTo assignedToName assignedAt status lastMessageAt lastMessagePreview lastMessageDirection
  lastInboundAt windowExpiresAt windowOpen unreadCount tags createdAt
`;

const MESSAGE_FIELDS = `
  id conversationId wamid direction type body templateName templateLanguage media location contacts
  reaction replyToWamid status error sentBy sentByName attempts timestamp
`;

const SEND_RESULT_FIELDS = `
  ok code error
  message { ${MESSAGE_FIELDS} }
  conversation { ${CONVERSATION_FIELDS} }
`;

// Queries ------------------------------------------------------------------------

export const WA_STATUS = gql`
  query WhatsAppStatus {
    whatsappStatus { enabled missing displayNumber canUseInbox canManage userId userName counts limits }
  }
`;

export const WA_CONVERSATIONS = gql`
  query WhatsAppConversations($filter: String, $search: String, $tag: String, $assignedTo: String, $page: Int, $pageSize: Int) {
    whatsappConversations(filter: $filter, search: $search, tag: $tag, assignedTo: $assignedTo, page: $page, pageSize: $pageSize) {
      items { ${CONVERSATION_FIELDS} }
      total page pageSize hasMore
    }
  }
`;

export const WA_CONVERSATION = gql`
  query WhatsAppConversation($id: String!) {
    whatsappConversation(id: $id) {
      conversation { ${CONVERSATION_FIELDS} }
      linked
      consent { waId optInStatus reachability optInAt optOutAt history }
      linkCandidates
      permissions
      emailEngaged
      emailEngagementRequired
    }
  }
`;

export const WA_MESSAGES = gql`
  query WhatsAppMessages($conversationId: String!, $before: String, $limit: Int) {
    whatsappMessages(conversationId: $conversationId, before: $before, limit: $limit) {
      items { ${MESSAGE_FIELDS} }
      hasMore
    }
  }
`;

export const WA_TEMPLATES = gql`
  query WhatsAppTemplates($refresh: Boolean) {
    whatsappTemplates(refresh: $refresh) {
      id name language category headerText bodyText footerText buttons variables paramCount sendable unsupportedReason
    }
  }
`;

export const WA_HISTORY = gql`
  query WhatsAppHistory($leadId: String, $clientId: String) {
    whatsappHistory(leadId: $leadId, clientId: $clientId) {
      conversations { ${CONVERSATION_FIELDS} }
      contacts
    }
  }
`;

export const WA_RECORD_SEARCH = gql`
  query WhatsAppRecordSearch($query: String!) {
    whatsappRecordSearch(query: $query)
  }
`;

export const WA_CONTACT_CONSENT = gql`
  query WhatsAppContactConsent($phone: String!) {
    whatsappContactConsent(phone: $phone)
  }
`;

export const WA_QUICK_REPLIES = gql`
  query WhatsAppQuickReplies {
    whatsappQuickReplies { id title shortcut body category scope ownerId usageCount updatedAt }
  }
`;

export const WA_ASSIGNEES = gql`
  query WhatsAppAssignees { whatsappAssignees }
`;

export const WA_TAGS = gql`
  query WhatsAppTags { whatsappTags }
`;

export const WA_AUDIT_LOG = gql`
  query WhatsAppAuditLog($conversationId: String, $limit: Int) {
    whatsappAuditLog(conversationId: $conversationId, limit: $limit) { id action actorName summary createdAt }
  }
`;

export const WA_ADMIN_OVERVIEW = gql`
  query WhatsAppAdminOverview($days: Int) {
    whatsappAdminOverview(days: $days) { config health stats perUser templateUsage webhook avgFirstResponseMinutes }
  }
`;

// Mutations ----------------------------------------------------------------------

export const WA_CLAIM = gql`
  mutation WhatsAppClaim($id: String!) { whatsappClaimConversation(id: $id) { ${CONVERSATION_FIELDS} } }
`;

export const WA_ASSIGN = gql`
  mutation WhatsAppAssign($id: String!, $userId: String) { whatsappAssignConversation(id: $id, userId: $userId) { ${CONVERSATION_FIELDS} } }
`;

export const WA_SET_STATUS = gql`
  mutation WhatsAppSetStatus($id: String!, $status: String!) { whatsappSetConversationStatus(id: $id, status: $status) { ${CONVERSATION_FIELDS} } }
`;

export const WA_LINK = gql`
  mutation WhatsAppLink($id: String!, $leadId: String, $leadContactId: String, $clientId: String) {
    whatsappLinkConversation(id: $id, leadId: $leadId, leadContactId: $leadContactId, clientId: $clientId) { ${CONVERSATION_FIELDS} }
  }
`;

export const WA_UNLINK = gql`
  mutation WhatsAppUnlink($id: String!, $which: String!) { whatsappUnlinkConversation(id: $id, which: $which) { ${CONVERSATION_FIELDS} } }
`;

export const WA_SEND_TEXT = gql`
  mutation WhatsAppSendText($conversationId: String!, $body: String!) {
    whatsappSendText(conversationId: $conversationId, body: $body) { ${SEND_RESULT_FIELDS} }
  }
`;

export const WA_SEND_TEMPLATE = gql`
  mutation WhatsAppSendTemplate($conversationId: String!, $templateName: String!, $language: String, $params: [String]) {
    whatsappSendTemplate(conversationId: $conversationId, templateName: $templateName, language: $language, params: $params) { ${SEND_RESULT_FIELDS} }
  }
`;

export const WA_START_CONVERSATION = gql`
  mutation WhatsAppStart($phone: String!, $contactName: String, $leadId: String, $leadContactId: String, $clientId: String, $templateName: String!, $language: String, $params: [String]) {
    whatsappStartConversation(phone: $phone, contactName: $contactName, leadId: $leadId, leadContactId: $leadContactId, clientId: $clientId, templateName: $templateName, language: $language, params: $params) { ${SEND_RESULT_FIELDS} }
  }
`;

export const WA_ADD_NOTE = gql`
  mutation WhatsAppAddNote($conversationId: String!, $body: String!) {
    whatsappAddNote(conversationId: $conversationId, body: $body) { ${MESSAGE_FIELDS} }
  }
`;

export const WA_RETRY = gql`
  mutation WhatsAppRetry($messageId: String!) { whatsappRetryMessage(messageId: $messageId) { ${MESSAGE_FIELDS} } }
`;

export const WA_MARK_READ = gql`
  mutation WhatsAppMarkRead($id: String!) { whatsappMarkRead(id: $id) { ${CONVERSATION_FIELDS} } }
`;

export const WA_SET_TAGS = gql`
  mutation WhatsAppSetTags($id: String!, $tags: [String]!) { whatsappSetTags(id: $id, tags: $tags) { ${CONVERSATION_FIELDS} } }
`;

export const WA_RENAME = gql`
  mutation WhatsAppRename($id: String!, $name: String!) { whatsappRenameContact(id: $id, name: $name) { ${CONVERSATION_FIELDS} } }
`;

export const WA_SET_OPT_IN = gql`
  mutation WhatsAppSetOptIn($phone: String!, $optIn: Boolean!, $note: String, $leadId: String) {
    whatsappSetOptIn(phone: $phone, optIn: $optIn, note: $note, leadId: $leadId)
  }
`;

export const WA_SAVE_QUICK_REPLY = gql`
  mutation WhatsAppSaveQuickReply($id: String, $title: String!, $shortcut: String, $body: String!, $category: String, $scope: String) {
    whatsappSaveQuickReply(id: $id, title: $title, shortcut: $shortcut, body: $body, category: $category, scope: $scope) {
      id title shortcut body category scope ownerId usageCount updatedAt
    }
  }
`;

export const WA_DELETE_QUICK_REPLY = gql`
  mutation WhatsAppDeleteQuickReply($id: String!) { whatsappDeleteQuickReply(id: $id) }
`;

export const WA_USE_QUICK_REPLY = gql`
  mutation WhatsAppUseQuickReply($id: String!) { whatsappUseQuickReply(id: $id) }
`;

export const WA_SAVE_TEMPLATE_CONFIG = gql`
  mutation WhatsAppSaveTemplateConfig($name: String!, $variables: JSON!) { whatsappSaveTemplateConfig(name: $name, variables: $variables) }
`;

export const WA_REFRESH_HEALTH = gql`
  mutation WhatsAppRefreshHealth { whatsappRefreshHealth }
`;

export const WA_SEND_RULES = gql`
  query WhatsAppSendRules { whatsappSendRules }
`;

export const WA_SAVE_SEND_RULES = gql`
  mutation WhatsAppSaveSendRules($templateGapHours: Int, $maxUnansweredTemplates: Int, $outboundPerUserPerHour: Int, $marketingRequiresEmailEngagement: Boolean) {
    whatsappSaveSendRules(
      templateGapHours: $templateGapHours
      maxUnansweredTemplates: $maxUnansweredTemplates
      outboundPerUserPerHour: $outboundPerUserPerHour
      marketingRequiresEmailEngagement: $marketingRequiresEmailEngagement
    )
  }
`;

// Template management (admin) ---------------------------------------------------

export const WA_TEMPLATES_ADMIN = gql`
  query WhatsAppTemplatesAdmin { whatsappTemplatesAdmin }
`;

export const WA_CREATE_TEMPLATE = gql`
  mutation WhatsAppCreateTemplate($draft: JSON!) { whatsappCreateTemplate(draft: $draft) }
`;

export const WA_EDIT_TEMPLATE = gql`
  mutation WhatsAppEditTemplate($id: String!, $draft: JSON!) { whatsappEditTemplate(id: $id, draft: $draft) }
`;

export const WA_DELETE_TEMPLATE = gql`
  mutation WhatsAppDeleteTemplate($id: String!) { whatsappDeleteTemplate(id: $id) }
`;

export const WA_MEDIA_LINKS = gql`
  query WhatsAppMediaLinks($ids: [String!]!) { whatsappMediaLinks(ids: $ids) }
`;
